import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Accordion, AccordionDetails, AccordionSummary, Box, styled, Typography } from '@mui/material';
import DOMPurify from 'dompurify';
import { useBreakpoints } from 'hooks';

import { ReactComponent as DropdownArrow } from 'assets/icons/DropdownArrow.svg';
import { ReactComponent as InformationCircleIcon } from 'assets/icons/InformationCircleIcon.svg';

import { IExampleModel, IInstructionsComponentProps } from './types';

import styles from './InstructionsComponent.module.scss';

import scssVariables from 'styles/sass-variables.module.scss';

const StyledAccordion = styled(Accordion)({
  '&.MuiPaper-root': {
    margin: 0,

    padding: '12px',
    color: `${scssVariables.text_primary}`,
    backgroundColor: 'transparent',
    boxShadow: 'none',
    border: `1px solid ${scssVariables.orange_default}`,
    borderRadius: '8px',
    '& .MuiAccordionSummary-root': {
      minHeight: 'fit-content',
      padding: 0,
    },
    '& .MuiAccordionSummary-content': {
      margin: 0,
    },
    '& .MuiAccordionDetails-root': {
      padding: 0,
    },
    '& .Mui-expanded': {
      minHeight: 'fit-content',
    },
    '&:last-child': {
      marginBottom: 0,
    },
    '&::before': {
      content: 'none',
    },
  },
});

const getInitialExpandedState = (examples: IExampleModel[]): string | false => {
  return examples.length ? examples[0].title : false;
};

export const InstructionsComponent: FC<IInstructionsComponentProps> = ({
  name,
  instruction,
  exampleOne,
  exampleTwo,
  learnMoreLink = 'https://growexa.com/help-and-support',
}) => {
  const { t } = useTranslation();
  const { isMobile } = useBreakpoints();

  const examples: IExampleModel[] = [
    { title: t('exampleOrder', { number: 1 }), content: exampleOne },
    { title: t('exampleOrder', { number: 2 }), content: exampleTwo },
  ];

  const [expanded, setExpanded] = useState(getInitialExpandedState(examples));

  useEffect(() => {
    setExpanded(getInitialExpandedState(examples));
  }, [name]);

  const handleChange = (panel: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };

  return (
    <Box className={styles.wrapper}>
      {!isMobile && (
        <Box className={styles.iconContainer}>
          <InformationCircleIcon />
        </Box>
      )}
      <Box className={styles.columnGrid}>
        <Typography variant="h5">{name}</Typography>
        {instruction && (
          <Typography
            component="div"
            variant="body2"
            className={styles.instructionParagraph}
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(instruction) }}
          />
        )}
        <Link className={styles.link} to={learnMoreLink} target="_blank">
          {t('learnMore')}
        </Link>

        {examples
          .filter((item) => item.content)
          .map((item) => (
            <StyledAccordion key={item.title} expanded={expanded === item.title} onChange={handleChange(item.title)}>
              <AccordionSummary expandIcon={<DropdownArrow />}>
                <Typography variant="h6" className={styles.summaryBadge}>
                  {item.title}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography
                  component="div"
                  variant="body2"
                  className={styles.details}
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(item.content ?? '') }}
                />
              </AccordionDetails>
            </StyledAccordion>
          ))}
      </Box>
    </Box>
  );
};
